/**
 * src/lib/voice/voiceMode.js — Voice interaction mode (Phase 90-06)
 *
 * Two modes:
 *   - 'auto'  Always-on listening with VAD auto-start/auto-end (walk-and-talk).
 *   - 'ptt'   Hold-to-speak via PTTButton. Original v0.x behavior.
 *
 * Default resolution:
 *   localStorage('acn_voice_mode')           — explicit user preference wins
 *   else window.location.pathname === '/voice' → 'auto'  (dedicated voice route)
 *   else 'ptt'                               — overlay shown above other views
 *
 * No server env override — voice mode is a per-device UX preference.
 */

import { useEffect, useState, useCallback } from 'react';

export const VOICE_MODES = ['auto', 'ptt'];
const LS_KEY = 'acn_voice_mode';
const VOICE_ROUTE = '/voice';

function readStored() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(LS_KEY);
    return VOICE_MODES.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function readRouteDefault() {
  if (typeof window === 'undefined' || !window.location) return 'ptt';
  return window.location.pathname === VOICE_ROUTE ? 'auto' : 'ptt';
}

export function getVoiceMode() {
  return readStored() ?? readRouteDefault();
}

export function setVoiceMode(mode) {
  if (!VOICE_MODES.includes(mode)) {
    throw new Error(`Invalid voice mode: ${mode}. Supported: ${VOICE_MODES.join(', ')}`);
  }
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(LS_KEY, mode); } catch { /* noop */ }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new StorageEvent('storage', { key: LS_KEY, newValue: mode }));
  }
}

export function useVoiceMode() {
  const [mode, setMode] = useState(getVoiceMode);

  useEffect(() => {
    function onStorage(e) {
      if (e.key === LS_KEY || e.key === null) {
        setMode(getVoiceMode());
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    return undefined;
  }, []);

  const update = useCallback((next) => {
    setVoiceMode(next);
    setMode(next);
  }, []);

  return [mode, update];
}
